import React, { useContext, useEffect, useReducer, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { MenuList, canSeeMenuItem, canSeeMenuGroup } from './Menu.jsx';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import {
  MdAccountCircle,
  MdClose,
  MdAssignment,
  MdAutorenew,
  MdBook,
  MdCalendarMonth,
  MdCheckCircle,
  MdContactPage,
  MdDescription,
  MdEmojiEvents,
  MdFactCheck,
  MdFolder,
  MdFormatListBulleted,
  MdGroups,
  MdHelp,
  MdHowToReg,
  MdInventory2,
  MdLayers,
  MdLink,
  MdMenuBook,
  MdMic,
  MdPublic,
  MdRateReview,
  MdSpaceDashboard,
  MdWork,
  MdWorkspacePremium,
} from 'react-icons/md';
import { ThemeContext } from '../../context/ThemeContext';

import './sidebar.css';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useDispatch, useSelector } from 'react-redux';
import { navtoggle } from '../../store/actions/AuthActions';

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

/* Filled Material glyphs, matching the top-level icons in Menu.jsx.
   lucide has no filled variant -- its icons are open stroke paths, so
   solid means a different family, not a prop. */
const getSubMenuIcon = (title) => {
  const size = 18;
  const icons = {
    'Calendar': <MdCalendarMonth size={size} className="lms-nav-icon" />,
    'Attendances': <MdFactCheck size={size} className="lms-nav-icon" />,
    'My Attendance Report': <MdFactCheck size={size} className="lms-nav-icon" />,
    'Session Feedback': <MdRateReview size={size} className="lms-nav-icon" />,
    'All Students': <MdGroups size={size} className="lms-nav-icon" />,
    'Trainers': <MdHowToReg size={size} className="lms-nav-icon" />,
    'Batches': <MdLayers size={size} className="lms-nav-icon" />,
    'Classrooms': <MdInventory2 size={size} className="lms-nav-icon" />,
    'Events': <MdPublic size={size} className="lms-nav-icon" />,
    'Syllabus': <MdMenuBook size={size} className="lms-nav-icon" />,
    'Course Modules': <MdFolder size={size} className="lms-nav-icon" />,
    'Ranking Dashboard': <MdEmojiEvents size={size} className="lms-nav-icon" />,
    'Students Report': <MdDescription size={size} className="lms-nav-icon" />,
    'Student Report': <MdDescription size={size} className="lms-nav-icon" />,
    'Batch Report': <MdAssignment size={size} className="lms-nav-icon" />,
    'Trainer Dashboard': <MdSpaceDashboard size={size} className="lms-nav-icon" />,
    'Attendance': <MdCheckCircle size={size} className="lms-nav-icon" />,
    'CourseWiseMcq': <MdHelp size={size} className="lms-nav-icon" />,
    'Quiz': <MdFormatListBulleted size={size} className="lms-nav-icon" />,
    'Quiz To Batch': <MdLink size={size} className="lms-nav-icon" />,
    'Mock Interview': <MdMic size={size} className="lms-nav-icon" />,
    'Student Dashboard': <MdSpaceDashboard size={size} className="lms-nav-icon" />,
    'Suggested Batches': <MdAutorenew size={size} className="lms-nav-icon" />,
    'Student Profile': <MdAccountCircle size={size} className="lms-nav-icon" />,
    'Student ID Card': <MdContactPage size={size} className="lms-nav-icon" />,
    'Job Opening Details': <MdWork size={size} className="lms-nav-icon" />,
    'Certificate': <MdWorkspacePremium size={size} className="lms-nav-icon" />,
    'My Quizzes': <MdBook size={size} className="lms-nav-icon" />,
    'Feedback': <MdRateReview size={size} className="lms-nav-icon" />,
    'Chat': <MdFormatListBulleted size={size} className="lms-nav-icon" />,
  };
  return icons[title] || null;
};

const initialState = {
  active: '',
  activeSubmenu: '',
};

const SideBar = () => {
  const { iconHover, sidebarposition, headerposition, sidebarLayout, ChangeIconSidebar, openMenuToggle } =
    useContext(ThemeContext);
  const [state, setState] = useReducer(reducer, initialState);
  const { hasPermission, user } = useAuth();
  const { unreadCounts } = useSocket();
  const dispatch = useDispatch();
  const sideMenu = useSelector(state => state.sideMenu);
  const navigate = useNavigate();
  const redirected = useRef(false);

  const handleCloseSidebar = () => {
    dispatch(navtoggle());
    if (openMenuToggle) {
      openMenuToggle();
    }
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768 && sideMenu) {
      handleCloseSidebar();
    }
  };

  // Still needed by the menu filter below, even though the account
  // button that used to sit at the sidebar's foot now lives in the topbar.
  const userData = user?.user || user;

  const isStudent = () =>
    userData?.profiles?.some((p) => (p.label || p.name) === 'Student');

  const isStudentOrTrainer = () => {
    return user?.profiles?.some((p) => p.label === 'Student' || p.label === 'Trainer');
  };

  // Who sees what is Menu.jsx's rule; these bind it to this person.
  const canSeeItem = (item) => canSeeMenuItem(item, hasPermission, isStudent());
  const canSeeGroup = (group) => canSeeMenuGroup(group, hasPermission, isStudent());

  const unreadTotal = Object.values(unreadCounts || {}).reduce(
    (acc, count) => acc + (count || 0),
    0
  );

  const handleMenuActive = (status) => {
    setState({ active: status });
    if (state.active === status) {
      setState({ active: '' });
    }
  };

  const handleSubmenuActive = (status) => {
    setState({ activeSubmenu: status });
    if (state.activeSubmenu === status) {
      setState({ activeSubmenu: '' });
    }
  };

  const [hideOnScroll, setHideOnScroll] = useState(true);
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll]
  );

  let path = window.location.pathname.replace(/^\//, '');

  useEffect(() => {
    if (!redirected.current) {
      if (path === '' || path === '/') {
        const dashboardItem = MenuList.find((item) => item.to && item.to.includes('dashboard'));
        if (dashboardItem && hasPermission(dashboardItem.eventKey, 'menu')) {
          const target = dashboardItem.to || MenuList[0]?.to;
          if (target) {
            navigate(target);
            redirected.current = true;
          }
        }
      }
    }

    MenuList.forEach((data) => {
      if (data.to === path) {
        setState({ active: data.title });
      } else {
        data.content?.forEach((item) => {
          if (path === item.to) {
            setState({ active: data.title });
          }
          item.content?.forEach((ele) => {
            if (path === ele.to) {
              setState({ activeSubmenu: item.title, active: data.title });
            }
          });
        });
      }
    });
  }, [path, navigate, hasPermission]);

  return (
    <>
      {/* Mobile backdrop overlay when sidebar is open on small devices */}
      {sideMenu && (
        <div
          className="lms-sidebar-backdrop"
          onClick={handleCloseSidebar}
          aria-hidden="true"
        />
      )}
      <div
        onMouseEnter={() => ChangeIconSidebar(true)}
        onMouseLeave={() => ChangeIconSidebar(false)}
        className={`deznav ${sideMenu ? 'lms-sidebar-hidden' : ''} ${iconHover} ${sidebarposition.value === 'fixed' &&
          sidebarLayout.value === 'horizontal' &&
          headerposition.value === 'static'
          ? hideOnScroll > 120
            ? 'fixed'
            : ''
          : ''
          }`}
      >

        {/* ── The brand, at the head of the rail ──────────────────────
            It used to be .nav-header: a separately fixed box pinned to
            the top-left corner, the same width as the rail but sitting
            in the topbar's 80px row. Two boxes in one strip, so it read
            as a block inside the bar rather than as the top of the
            sidebar -- and it had to outrank the bar's opaque background
            at z-index 11 to stay visible at all.

            The column is one piece from the top of the screen down, the
            gradient starts where the screen does, and the topbar begins
            where the rail ends instead of being painted across it.

            It sits INSIDE .deznav-scroll rather than beside it. That is
            what keeps it lined up with the menu: the scroller is what
            loses width when a scrollbar appears, so a head outside it
            kept its full width while every nav item below shrank, and
            the two stopped agreeing on where 22px was. Inside, they
            shrink together. `position: sticky` in the stylesheet is
            what stops it scrolling away now that it is in the scroll
            area.

            This was the mobile drawer's header before; the drawer keeps
            it, and the close button is the only part still phone-only.
            ---------------------------------------------------------- */}
        <div className="deznav-scroll">
          <div className="lms-rail-head">
            <Link
              to="/dashboard"
              className="brand-logo lms-brand-link"
              onClick={handleLinkClick}
            >
              <div className="lms-brand-mark">
                <img src="/Logo.png" alt="" aria-hidden="true" />
              </div>
              <div className="lms-brand-name">
                SevenMentor
                <small>Learning Hub</small>
              </div>
            </Link>

            <button
              type="button"
              className="lms-sidebar-close-btn"
              onClick={handleCloseSidebar}
              aria-label="Close sidebar"
            >
              <MdClose size={18} />
            </button>
          </div>

          <ul className="metismenu" id="menu">
            {MenuList.map((data, index) => {
              if (
                  canSeeGroup(data) ||
                  (data.eventKey === 'chat' && isStudentOrTrainer()) ||
                  // Everyone signed in has their own notification inbox, so
                  // this entry isn't gated on a role permission. Appearance
                  // is the same kind of thing: a preference in this person's
                  // own browser, visible to nobody else.
                  data.eventKey === 'notifications' ||
                  data.eventKey === 'appearance'
              ) {
                if (data.classsChange === 'menu-title') {
                  return (
                    <li className={`nav-label  ${data.classsChange} ${data.extraclass}`} key={index}>
                      {data.title}
                    </li>
                  );
                }

                return (
                  <li
                    className={`has-menu ${state.active === data.title ? 'mm-active' : ''} ${data.to === path ? 'mm-active' : ''
                      }`}
                    key={index}
                  >
                    {data.content && data.content.length > 0 ? (
                      <>
                        <Link
                          to="#"
                          // Only the module groups and CRM Menu carry the
                          // arrow (lms-nav-arrow); every other expandable row
                          // draws none. aria-expanded is what turns it: down
                          // while closed, up while open.
                          className={`has-arrow ai-icon${
                            ['LMS', 'Trainer Module', 'Student Module', 'CRM Menu'].includes(data.title)
                              ? ' lms-nav-arrow'
                              : ''
                          }`}
                          aria-expanded={state.active === data.title}
                          onClick={() => handleMenuActive(data.title)}
                        >
                          {data.iconStyle}{' '}
                          <span className="nav-text">
                            {data.title}
                            {data.eventKey === 'chat' && unreadTotal > 0 ? (
                              <span className="badge badge-danger lms-nav-badge">
                                {unreadTotal}
                              </span>
                            ) : (
                              <span className="badge badge-danger lms-nav-badge">
                                {data.update}
                              </span>
                            )}
                          </span>
                        </Link>

                        <Collapse in={state.active === data.title}>
                          <ul className={`${data.classsChange === 'mm-collapse' ? 'mm-show' : ''}`}>
                            {data.content
                              .filter(canSeeItem)
                              // A student's own page (My Attendance Report): shown to a
                              // student alone, even to staff who share its permission.
                              .filter((item) => !item.studentOnly || isStudent())
                              .map((item, subIndex) => (
                                <li
                                  key={subIndex}
                                  className={`${state.activeSubmenu === item.title ? 'mm-active' : ''
                                    } ${item.to === path ? 'mm-active' : ''}`}
                                >
                                  {item.content && item.content.length > 0 ? (
                                    <>
                                      {/* No wrapper: the <a> is already
                                          display:flex with a gap in
                                          sidebar.css, so the icon and the
                                          label are its own flex children.
                                          The div that used to hold them
                                          carried d-flex, which this project
                                          does not define -- it was a plain
                                          block, and the icon sat on the
                                          text baseline instead of centred. */}
                                      <Link
                                        to={item.to}
                                        className={item.hasMenu ? 'has-arrow' : ''}
                                        aria-expanded={
                                          item.hasMenu
                                            ? state.activeSubmenu === item.title
                                            : undefined
                                        }
                                        onClick={() => handleSubmenuActive(item.title)}
                                      >
                                        {getSubMenuIcon(item.title)}
                                        <span>{item.title}</span>
                                      </Link>

                                      <Collapse in={state.activeSubmenu === item.title}>
                                        <ul
                                          className={`${data.classsChange === 'mm-collapse' ? 'mm-show' : ''
                                            }`}
                                        >
                                          {item.content
                                            .filter((child) => hasPermission(child.eventKey, 'menu'))
                                            .map((child, childIndex) => (
                                              <li key={childIndex}>
                                                <Link
                                                  className={path === child.to ? 'mm-active' : ''}
                                                  to={child.to}
                                                  onClick={handleLinkClick}
                                                >
                                                  {getSubMenuIcon(child.title)}
                                                  <span>{child.title}</span>
                                                </Link>
                                              </li>
                                            ))}
                                        </ul>
                                      </Collapse>
                                    </>
                                  ) : (
                                    <Link
                                      to={item.to}
                                      className={item.to === path ? 'mm-active' : ''}
                                      onClick={handleLinkClick}
                                    >
                                      {getSubMenuIcon(item.title)}
                                      <span>
                                        {item.title}
                                        {item.eventKey === 'chat' && unreadTotal > 0 && (
                                          <span className="badge badge-danger lms-nav-badge">
                                            {unreadTotal}
                                          </span>
                                        )}
                                      </span>
                                    </Link>
                                  )}
                                </li>
                              ))}
                          </ul>
                        </Collapse>
                      </>
                    ) : (
                      <Link
                        to={data.to}
                        className={`${data.to === path ? 'mm-active' : ''}`}
                        onClick={handleLinkClick}
                      >
                        {data.iconStyle}{' '}
                        <span className="nav-text">
                          {data.title}
                          {data.eventKey === 'chat' && unreadTotal > 0 ? (
                              <span className="badge badge-danger lms-nav-badge">
                                  {unreadTotal}
                              </span>
                          ) : (
                              <span className="badge badge-danger lms-nav-badge">
                                  {data.update}
                              </span>
                          )}
                        </span>
                      </Link>
                    )}
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>

      </div>
    </>
  );
};

export default SideBar;
